/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/

/**
 * Browser client for the voice agent WebSocket (server: src/voice-agent/).
 *
 * Standalone on purpose — no React and no app imports — so it can be reused in
 * any web app. It owns the socket, the auth handshake, microphone capture
 * (AudioWorklet → PCM16 at the requested rate), and gapless playback of the
 * agent's audio. Everything the server sends is re-emitted as events with the
 * same names (see docs/agent/modules/voice-agent.md §6).
 *
 *   const client = new VoiceAgentClient({ url, getToken, sessionMetadata: { agentId } });
 *   client.on('agent.text.delta', ({ text }) => ...);
 *   await client.connect();          // auth → session.start → session.ready
 *   await client.startMicrophone();  // call from a user gesture
 *   ...
 *   await client.disconnect();
 */

export const AUDIO_FRAME_HEADER_BYTES = 8;
export const AUDIO_FRAME_VERSION = 1;
const HEARTBEAT_MS = 15000;
const CONNECT_TIMEOUT_MS = 15000;
const LEVEL_EVENT_MS = 66; // ~15 Hz meter updates
const PLAYBACK_LEAD_S = 0.03; // small lead so the first buffer is not clipped

/**
 * Parses a server audio frame: u8 version | u8 kind | u16 chunkSeq | u32 turnSeq
 * (big-endian), then the audio payload. Null when too short or of an unknown version.
 */
export function decodeAudioFrameHeader(buffer) {
  if (!(buffer instanceof ArrayBuffer) || buffer.byteLength < AUDIO_FRAME_HEADER_BYTES) return null;
  const view = new DataView(buffer);
  const version = view.getUint8(0);
  if (version !== AUDIO_FRAME_VERSION) return null;
  return {
    version,
    kind: view.getUint8(1),
    chunkSeq: view.getUint16(2),
    turnSeq: view.getUint32(4),
    payload: buffer.slice(AUDIO_FRAME_HEADER_BYTES),
  };
}

/** Float32 samples (-1..1) → Int16 PCM. */
export function floatTo16BitPCM(input) {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

/** Int16 PCM (little-endian ArrayBuffer or Int16Array) → Float32 samples. */
export function pcm16ToFloat32(input) {
  const samples = input instanceof Int16Array ? input : new Int16Array(input, 0, Math.floor(input.byteLength / 2));
  const out = new Float32Array(samples.length);
  for (let i = 0; i < samples.length; i += 1) out[i] = samples[i] / 0x8000;
  return out;
}

/**
 * Streaming linear-interpolation resampler. `push(block)` takes Float32 input
 * at `fromRate` and returns the Float32 output at `toRate` for that block,
 * carrying the fractional position across blocks.
 *
 * Self-contained (no outer references): its source is also injected into the
 * AudioWorklet below, so keep it plain.
 */
export function createResampler(fromRate, toRate) {
  var ratio = fromRate / toRate;
  var pos = 0;
  var last = 0;
  return {
    push: function (block) {
      var n = block.length;
      var out = [];
      while (pos < n - 1 || (pos < 0 && n > 0)) {
        var idx = Math.floor(pos);
        var frac = pos - idx;
        var a = idx < 0 ? last : block[idx];
        var b = block[idx + 1];
        out.push(a + (b - a) * frac);
        pos += ratio;
      }
      if (n > 0) {
        pos -= n;
        last = block[n - 1];
      }
      return new Float32Array(out);
    },
  };
}

/** One-shot (non-streaming) resample, for tests and utilities. */
export function downsample(input, fromRate, toRate) {
  if (fromRate === toRate) return input;
  return createResampler(fromRate, toRate).push(input);
}

/** True when this browser can run the voice client. */
export function isVoiceAgentSupported() {
  return typeof window !== 'undefined'
    && typeof window.WebSocket === 'function'
    && typeof window.AudioWorkletNode === 'function'
    && !!window.navigator?.mediaDevices?.getUserMedia;
}

/**
 * True when the browser can route audio to a chosen speaker: AudioContext.setSinkId
 * (Chrome/Edge 110+) or HTMLMediaElement.setSinkId. Safari has neither, so the
 * system default output is used there.
 */
export function supportsAudioOutputSelection() {
  if (typeof window === 'undefined') return false;
  return typeof window.AudioContext?.prototype?.setSinkId === 'function'
    || typeof window.HTMLMediaElement?.prototype?.setSinkId === 'function';
}

/**
 * Lists audio inputs (microphones) and outputs (speakers) as
 * { inputs: [{ deviceId, label }], outputs: [...] }.
 *
 * Browsers hide device labels until microphone permission is granted; with
 * `requestPermission` a short getUserMedia call asks for it first (its tracks
 * are stopped right away). Chrome's "communications" pseudo-device is left out
 * (it duplicates a real device); "default" is kept as "System default".
 */
export async function listAudioDevices({ requestPermission = false } = {}) {
  const mediaDevices = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
  if (!mediaDevices?.enumerateDevices) return { inputs: [], outputs: [] };
  if (requestPermission) {
    const stream = await mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
  }
  const devices = await mediaDevices.enumerateDevices();
  const pick = (kind, fallback) => devices
    .filter((d) => d.kind === kind && d.deviceId && d.deviceId !== 'communications')
    .map((d, index) => ({
      deviceId: d.deviceId,
      label: d.deviceId === 'default'
        ? `System default${d.label ? ` (${d.label.replace(/^Default\s*-\s*/i, '')})` : ''}`
        : d.label || `${fallback} ${index + 1}`,
    }));
  return { inputs: pick('audioinput', 'Microphone'), outputs: pick('audiooutput', 'Speaker') };
}

/**
 * AudioWorklet processor: resamples the mic to `targetRate`, converts to PCM16
 * and posts fixed-size frames (`frameSamples`) with their RMS level.
 */
export const WORKLET_SOURCE = `
const createResampler = (${createResampler.toString()});
class VoiceCaptureProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const { targetRate, frameSamples } = options.processorOptions;
    this.resampler = createResampler(sampleRate, targetRate);
    this.frame = new Int16Array(frameSamples);
    this.filled = 0;
    this.sumSquares = 0;
  }
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (!channel) return true;
    const samples = this.resampler.push(channel);
    for (let i = 0; i < samples.length; i += 1) {
      const s = Math.max(-1, Math.min(1, samples[i]));
      this.sumSquares += s * s;
      this.frame[this.filled++] = s < 0 ? s * 0x8000 : s * 0x7fff;
      if (this.filled === this.frame.length) {
        const pcm = this.frame.buffer.slice(0);
        const rms = Math.sqrt(this.sumSquares / this.frame.length);
        this.port.postMessage({ pcm, rms }, [pcm]);
        this.filled = 0;
        this.sumSquares = 0;
      }
    }
    return true;
  }
}
registerProcessor('voice-capture', VoiceCaptureProcessor);
`;

/** Error with the close code/reason of a failed connect. */
export class VoiceAgentConnectError extends Error {
  constructor(message, { code, reason } = {}) {
    super(message);
    this.name = 'VoiceAgentConnectError';
    this.code = code;
    this.reason = reason;
  }
}

export class VoiceAgentClient {
  /**
   * @param {object} options
   * @param {string} options.url ws(s) URL of the voice endpoint, e.g. wss://api.example.com/ws/voice
   * @param {() => string|Promise<string>} options.getToken the access token sent in the first message
   * @param {object} [options.sessionMetadata] sent in session.start (e.g. { agentId, accountId })
   * @param {number} [options.inputSampleRate=16000]
   * @param {number} [options.frameMs=20]
   * @param {string} [options.language]
   * @param {boolean} [options.debug=false] log every message to the console
   * @param {string} [options.inputDeviceId] microphone to capture (default: the system default)
   * @param {string} [options.outputDeviceId] speaker to play on, when supportsAudioOutputSelection()
   * @param {AudioContext} [options.audioContext] an AudioContext created by the caller in a user
   *   gesture; it is used but not closed by disconnect()
   */
  constructor({
    url,
    getToken,
    sessionMetadata = {},
    inputSampleRate = 16000,
    frameMs = 20,
    language,
    debug = false,
    inputDeviceId,
    outputDeviceId,
    audioContext,
  }) {
    this.url = url;
    this.getToken = getToken;
    this.sessionMetadata = sessionMetadata;
    this.inputSampleRate = inputSampleRate;
    this.frameSamples = Math.round((inputSampleRate * frameMs) / 1000);
    this.language = language;
    this.debug = debug;

    this.listeners = new Map();
    this.status = 'idle'; // idle | connecting | authenticating | ready | closed
    this.ws = null;
    this.sessionId = null;
    this.outputFormat = null;
    this.ctx = audioContext || null;
    this.ownsContext = !audioContext;
    this.inputDeviceId = inputDeviceId || '';
    this.outputDeviceId = outputDeviceId || '';
    this.outputElement = null; // fallback route for HTMLMediaElement.setSinkId
    this.outputNode = null;
    this.muted = false;
    this.mic = null; // { stream, source, node, sink }
    this.micGeneration = 0; // bumped by stopMicrophone(); a pending start that sees a new value gives up
    this.playback = null; // current agent turn being played
    this.interruptedSeqs = new Set();
    this.lastLevelAt = 0;
    this.heartbeat = null;
    this.awaitingPong = false;
    this.connecting = null;
  }

  // ---- events ---------------------------------------------------------------

  /** Subscribes to an event; returns an unsubscribe function. */
  on(type, handler) {
    if (!this.listeners.has(type)) this.listeners.set(type, new Set());
    this.listeners.get(type).add(handler);
    return () => this.off(type, handler);
  }

  off(type, handler) {
    this.listeners.get(type)?.delete(handler);
  }

  emit(type, payload) {
    if (this.debug && type !== 'level') console.debug('[voice]', type, payload); // eslint-disable-line no-console
    this.listeners.get(type)?.forEach((handler) => {
      try {
        handler(payload);
      } catch (err) {
        console.error('[voice] listener failed', err); // eslint-disable-line no-console
      }
    });
    this.listeners.get('*')?.forEach((handler) => handler({ type, ...payload }));
  }

  setStatus(status) {
    if (this.status === status) return;
    this.status = status;
    this.emit('connection', { status });
  }

  // ---- connection -----------------------------------------------------------

  /**
   * Opens the socket and runs the handshake: `auth` → `auth.ok`, then
   * `session.start` → `session.ready`. Rejects with VoiceAgentConnectError
   * (carrying the close code, e.g. 4401 / 4403) when the server refuses.
   * Call from a user gesture so the AudioContext can start.
   */
  connect() {
    if (this.status === 'ready') return Promise.resolve();
    if (this.connecting) return this.connecting;
    this.ensureAudioContext();
    this.connecting = this.runHandshake().finally(() => {
      this.connecting = null;
    });
    return this.connecting;
  }

  async runHandshake() {
    this.setStatus('connecting');
    this.interruptedSeqs.clear();
    const ws = new WebSocket(this.url);
    ws.binaryType = 'arraybuffer';
    this.ws = ws;

    const waitFor = (type) => new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        cleanup();
        reject(new VoiceAgentConnectError('The voice service did not respond'));
      }, CONNECT_TIMEOUT_MS);
      const onMessage = (event) => {
        if (typeof event.data !== 'string') return;
        let message;
        try { message = JSON.parse(event.data); } catch { return; }
        if (message.type === type) { cleanup(); resolve(message); }
      };
      const onClose = (event) => {
        cleanup();
        reject(new VoiceAgentConnectError(event.reason || 'Connection closed', { code: event.code, reason: event.reason }));
      };
      const cleanup = () => {
        clearTimeout(timer);
        ws.removeEventListener('message', onMessage);
        ws.removeEventListener('close', onClose);
      };
      ws.addEventListener('message', onMessage);
      ws.addEventListener('close', onClose);
    });

    ws.addEventListener('message', (event) => this.onMessage(event));
    ws.addEventListener('close', (event) => this.onClose(event));

    await new Promise((resolve, reject) => {
      const onOpen = () => { cleanup(); resolve(); };
      const onFail = (event) => {
        cleanup();
        reject(new VoiceAgentConnectError('Could not connect to the voice service', { code: event?.code, reason: event?.reason }));
      };
      const cleanup = () => {
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onFail);
        ws.removeEventListener('close', onFail);
      };
      ws.addEventListener('open', onOpen);
      ws.addEventListener('error', onFail);
      ws.addEventListener('close', onFail);
    });

    this.setStatus('authenticating');
    const token = await this.getToken();
    const authOk = waitFor('auth.ok');
    this.send({ type: 'auth', token });
    await authOk;

    const ready = waitFor('session.ready');
    this.send({
      type: 'session.start',
      inputFormat: { encoding: 'pcm16', sampleRate: this.inputSampleRate, channels: 1 },
      ...(this.language && { language: this.language }),
      metadata: this.sessionMetadata,
    });
    const session = await ready;
    this.sessionId = session.sessionId;
    this.outputFormat = session.outputFormat;
    this.setStatus('ready');
    this.startHeartbeat();
  }

  /** Ends the session: closes the socket, the microphone and audio. Idempotent. */
  async disconnect() {
    this.stopHeartbeat();
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.send({ type: 'session.end' });
      this.ws.close(1000, 'Client ended session');
    } else if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
      this.ws.close();
    }
    this.ws = null;
    this.stopMicrophone();
    this.stopPlayback(false);
    this.teardownOutputRoute();
    if (this.ctx) {
      const ctx = this.ctx;
      this.ctx = null;
      this.workletLoaded = null;
      if (this.ownsContext) {
        try { await ctx.close(); } catch { /* already closed */ }
      }
    }
    this.setStatus('closed');
  }

  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(message));
  }

  /** Push-to-talk: end the user's turn now. */
  commit() { this.send({ type: 'input.commit' }); }

  /** Typed turn over the same session. */
  sendText(text) {
    if (text && text.trim()) this.send({ type: 'text.input', text: text.trim() });
  }

  /** Stop the agent (stop button): server cancels the reply; local playback stops now. */
  interrupt() {
    this.send({ type: 'response.cancel' });
    this.stopPlayback(true);
  }

  /** Renew the credential without reconnecting. */
  refreshAuth(token) { this.send({ type: 'auth.refresh', token }); }

  startHeartbeat() {
    this.stopHeartbeat();
    this.awaitingPong = false;
    this.heartbeat = setInterval(() => {
      if (this.awaitingPong) {
        this.ws?.close(4408, 'No pong');
        return;
      }
      this.awaitingPong = true;
      this.send({ type: 'ping' });
    }, HEARTBEAT_MS);
  }

  stopHeartbeat() {
    clearInterval(this.heartbeat);
    this.heartbeat = null;
  }

  onClose(event) {
    if (this.ws && event.target !== this.ws) return; // an older socket
    this.stopHeartbeat();
    this.stopMicrophone();
    this.stopPlayback(false);
    this.ws = null;
    const wasActive = this.status !== 'closed';
    this.setStatus('closed');
    if (wasActive) this.emit('closed', { code: event.code, reason: event.reason });
  }

  // ---- inbound --------------------------------------------------------------

  onMessage(event) {
    if (typeof event.data !== 'string') {
      this.onAudioFrame(event.data);
      return;
    }
    let message;
    try { message = JSON.parse(event.data); } catch { return; }

    switch (message.type) {
      case 'pong':
        this.awaitingPong = false;
        break;
      case 'agent.response.started':
        // A new reply replaces whatever is still playing.
        this.stopPlayback(false);
        this.playback = {
          turnId: message.turnId,
          turnSeq: message.turnSeq,
          sources: new Set(),
          nextTime: 0,
          startedAt: null,
          scheduledMs: 0,
          audioCompleted: false,
          reported: false,
        };
        break;
      case 'agent.audio.completed':
        if (this.playback?.turnSeq === message.turnSeq) {
          this.playback.audioCompleted = true;
          this.maybeReportPlaybackCompleted();
        }
        break;
      case 'agent.interrupted':
        this.interruptedSeqs.add(message.turnSeq);
        if (this.playback?.turnSeq === message.turnSeq) this.stopPlayback(true);
        break;
      default:
        break;
    }
    this.emit(message.type, message);
  }

  onAudioFrame(data) {
    const frame = decodeAudioFrameHeader(data);
    const turn = this.playback;
    // Stale audio (an interrupted or superseded reply) is never played.
    if (!frame || !turn || frame.turnSeq !== turn.turnSeq || this.interruptedSeqs.has(frame.turnSeq)) return;
    if (this.outputFormat?.encoding !== 'pcm16') {
      if (!this.warnedFormat) {
        this.warnedFormat = true;
        this.emit('error', { code: 'UNSUPPORTED_AUDIO', message: 'Unsupported audio format from the voice service', source: 'client', fatal: false });
      }
      return;
    }
    this.schedule(turn, pcm16ToFloat32(frame.payload));
  }

  // ---- playback -------------------------------------------------------------

  ensureAudioContext() {
    if (!this.ctx && typeof window !== 'undefined' && window.AudioContext) {
      this.ctx = new window.AudioContext({ latencyHint: 'interactive' });
      this.ownsContext = true;
      if (this.outputDeviceId) this.applyOutputDevice().catch(() => {});
    }
    if (this.ctx && this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    return this.ctx;
  }

  /**
   * Where agent audio is connected: the context destination, or — when only
   * HTMLMediaElement.setSinkId exists — a stream played by a hidden <audio>
   * element routed to the chosen speaker.
   */
  playbackDestination() {
    const ctx = this.ctx;
    if (!this.outputDeviceId || typeof ctx.setSinkId === 'function' || !supportsAudioOutputSelection()) {
      return ctx.destination;
    }
    if (!this.outputNode) {
      this.outputNode = ctx.createMediaStreamDestination();
      this.outputElement = new Audio();
      this.outputElement.srcObject = this.outputNode.stream;
      this.outputElement.setSinkId(this.outputDeviceId).catch(() => {});
      this.outputElement.play().catch(() => {});
    }
    return this.outputNode;
  }

  teardownOutputRoute() {
    if (this.outputElement) {
      this.outputElement.pause();
      this.outputElement.srcObject = null;
    }
    this.outputElement = null;
    this.outputNode = null;
  }

  async applyOutputDevice() {
    const ctx = this.ctx;
    if (!ctx) return;
    if (typeof ctx.setSinkId === 'function') {
      await ctx.setSinkId(this.outputDeviceId || '');
    } else if (this.outputElement) {
      await this.outputElement.setSinkId(this.outputDeviceId || '');
    }
  }

  /** Plays the agent on another speaker (takes effect immediately, also mid-reply). */
  async setOutputDevice(deviceId) {
    this.outputDeviceId = deviceId || '';
    await this.applyOutputDevice();
    this.emit('devices', { inputDeviceId: this.inputDeviceId, outputDeviceId: this.outputDeviceId });
  }

  /** Captures from another microphone; restarts capture when the mic is on. */
  async setInputDevice(deviceId) {
    this.inputDeviceId = deviceId || '';
    if (this.mic) {
      this.stopMicrophone();
      await this.startMicrophone();
    }
    this.emit('devices', { inputDeviceId: this.inputDeviceId, outputDeviceId: this.outputDeviceId });
  }

  schedule(turn, samples) {
    const ctx = this.ensureAudioContext();
    if (!ctx || !samples.length) return;
    const buffer = ctx.createBuffer(1, samples.length, this.outputFormat.sampleRate);
    buffer.copyToChannel(samples, 0);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.playbackDestination());

    const startAt = Math.max(ctx.currentTime + PLAYBACK_LEAD_S, turn.nextTime);
    if (turn.startedAt === null) {
      turn.startedAt = startAt;
      this.emit('playback', { playing: true, turnId: turn.turnId, turnSeq: turn.turnSeq });
    }
    turn.nextTime = startAt + buffer.duration;
    turn.scheduledMs += buffer.duration * 1000;
    turn.sources.add(source);
    source.onended = () => {
      turn.sources.delete(source);
      if (turn === this.playback) this.maybeReportPlaybackCompleted();
    };
    source.start(startAt);
  }

  /** Tells the server the reply finished playing (lets it return to listening at once). */
  maybeReportPlaybackCompleted() {
    const turn = this.playback;
    if (!turn || turn.reported || !turn.audioCompleted || turn.sources.size) return;
    turn.reported = true;
    this.send({ type: 'playback.completed', turnId: turn.turnId });
    this.emit('playback', { playing: false, turnId: turn.turnId, turnSeq: turn.turnSeq });
  }

  /**
   * Stops the current reply's audio. With `report`, tells the server how much
   * was heard (`playback.interrupted`) so its history keeps only that.
   */
  stopPlayback(report) {
    const turn = this.playback;
    if (!turn) return;
    this.playback = null;
    this.interruptedSeqs.add(turn.turnSeq);
    turn.sources.forEach((source) => {
      try { source.onended = null; source.stop(); } catch { /* not started */ }
    });
    turn.sources.clear();
    if (report && !turn.reported) {
      const playedMs = turn.startedAt === null || !this.ctx
        ? 0
        : Math.max(0, Math.min((this.ctx.currentTime - turn.startedAt) * 1000, turn.scheduledMs));
      turn.reported = true;
      this.send({ type: 'playback.interrupted', turnId: turn.turnId, playedMs: Math.round(playedMs) });
    }
    if (turn.startedAt !== null) this.emit('playback', { playing: false, turnId: turn.turnId, turnSeq: turn.turnSeq });
  }

  // ---- microphone -----------------------------------------------------------

  /**
   * Starts streaming the microphone. Call from a user gesture. Resolves without
   * opening the mic when stopMicrophone()/disconnect() runs while it is starting.
   */
  async startMicrophone() {
    if (this.mic) return;
    const generation = this.micGeneration;
    const cancelled = () => generation !== this.micGeneration;
    const ctx = this.ensureAudioContext();
    if (!ctx) throw new Error('Audio is not supported in this browser');
    const constraints = { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true };
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: this.inputDeviceId && this.inputDeviceId !== 'default'
          ? { ...constraints, deviceId: { exact: this.inputDeviceId } }
          : constraints,
      });
    } catch (err) {
      // The chosen microphone was unplugged: fall back to the system default.
      if (err?.name !== 'OverconstrainedError' && err?.name !== 'NotFoundError') throw err;
      if (!this.inputDeviceId) throw err;
      this.inputDeviceId = '';
      stream = await navigator.mediaDevices.getUserMedia({ audio: constraints });
      this.emit('devices', { inputDeviceId: '', outputDeviceId: this.outputDeviceId, fallback: true });
    }
    if (cancelled()) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    try {
      if (!this.workletLoaded) {
        const url = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }));
        this.workletLoaded = ctx.audioWorklet.addModule(url).finally(() => URL.revokeObjectURL(url));
      }
      await this.workletLoaded;
      if (cancelled()) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const source = ctx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(ctx, 'voice-capture', {
        processorOptions: { targetRate: this.inputSampleRate, frameSamples: this.frameSamples },
      });
      // Keep the node pulled by the graph without making it audible.
      const sink = ctx.createGain();
      sink.gain.value = 0;
      source.connect(node);
      node.connect(sink);
      sink.connect(ctx.destination);
      node.port.onmessage = ({ data }) => this.onCapturedFrame(data);
      this.mic = { stream, source, node, sink };
      this.applyMuteToTracks(); // a restarted mic (device switch) stays muted
      this.emit('mic', { on: true, muted: this.muted });
    } catch (err) {
      stream.getTracks().forEach((track) => track.stop());
      throw err;
    }
  }

  onCapturedFrame({ pcm, rms }) {
    const now = Date.now();
    if (now - this.lastLevelAt >= LEVEL_EVENT_MS) {
      this.lastLevelAt = now;
      this.emit('level', { rms: this.muted ? 0 : rms });
    }
    if (!this.muted && this.status === 'ready' && this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(pcm);
    }
  }

  stopMicrophone() {
    this.micGeneration += 1; // also cancels a startMicrophone() still in progress
    if (!this.mic) return;
    const { stream, source, node, sink } = this.mic;
    this.mic = null;
    node.port.onmessage = null;
    try { source.disconnect(); node.disconnect(); sink.disconnect(); } catch { /* already disconnected */ }
    stream.getTracks().forEach((track) => track.stop());
    this.emit('mic', { on: false, muted: this.muted });
  }

  /**
   * Mutes or unmutes the microphone without leaving the session: while muted no
   * audio frames are sent (the server hears nothing) and the track itself is
   * disabled, so nothing is captured. Persists across a mic restart
   * (e.g. switching devices).
   */
  setMuted(muted) {
    this.muted = !!muted;
    this.applyMuteToTracks();
    if (this.muted) this.emit('level', { rms: 0 });
    this.emit('mic', { on: !!this.mic, muted: this.muted });
  }

  applyMuteToTracks() {
    this.mic?.stream.getAudioTracks().forEach((track) => { track.enabled = !this.muted; });
  }
}
